import {
  Snackbar,
  Alert,
  type AlertColor,
  type AlertPropsColorOverrides,
} from '@mui/material';
import type { OverridableStringUnion } from '@mui/types';
import {
  createContext,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from 'react';

type SnackbarContextType = {
  snackbarOpen: boolean;
  /**
   *
   * @param message The message to be shown in the snackbar.
   * @param severity
   * @param duration How long the snackbar stays open for. It is 6 seconds by default. Leave this as null if you want it to permanently stay open.
   * @returns
   */
  handleSnackbarOpen: (
    message: string,
    severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined,
    duration?: number | null,
  ) => void;
  handleSnackbarClose: () => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  snackbarOpen: false,
  handleSnackbarOpen: () => {},
  handleSnackbarClose: () => {},
});

export const SnackbarContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarDuration, setSnackbarDuration] = useState<number | null>(6000);
  const [message, setMessage] = useState<string>();
  const [severity, setSeverity] = useState<
    OverridableStringUnion<AlertColor, AlertPropsColorOverrides> | undefined
  >();
  const handleSnackbarOpen = (
    message: string,
    severity:
      | OverridableStringUnion<AlertColor, AlertPropsColorOverrides>
      | undefined,
    duration: number | null = 6000,
  ) => {
    setMessage(message);
    setSeverity(severity);
    setSnackbarOpen(true);
    setSnackbarDuration(duration);
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <SnackbarContext.Provider
      value={useMemo(
        () => ({
          snackbarOpen,
          handleSnackbarOpen,
          handleSnackbarClose,
        }),
        [snackbarOpen],
      )}>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        autoHideDuration={snackbarDuration}
        onClose={handleSnackbarClose}
        sx={{
          maxWidth: { md: '40%' },
        }}>
        <Alert
          onClose={handleSnackbarClose}
          severity={severity}
          variant="filled"
          sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
      {children}
    </SnackbarContext.Provider>
  );
};
